// Zalo deep-link share button — Piece C of the wedge features batch.
//
// Behavior:
//  - If the browser exposes `navigator.share`, call it (mobile shows the
//    OS sheet; the user picks Zalo from the list).
//  - Otherwise copy the formatted text to the clipboard and surface a
//    toast: "Đã sao chép. Mở Zalo và dán vào hội thoại."
//
// The "Open Zalo Web" fallback is a best-effort link to chat.zalo.me;
// it doesn't auto-paste, but pre-opens the right tab for the user.

import { useState } from "react";
import { Share2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/lib/auth-store";
import { formatPropertySummary } from "@/lib/format-property-summary";
import { shareOrCopy } from "@/lib/share";
import type { Property } from "@/types/property";

interface ZaloShareButtonProps {
  property: Property;
  /** Optional override text. Defaults to the standard summary. */
  textOverride?: string | undefined;
  /** Optional agent name override (otherwise reads from auth store). */
  agentName?: string | undefined;
  /** Optional agent phone override. */
  agentPhone?: string | undefined;
  className?: string | undefined;
  variant?: "default" | "outline" | "secondary" | undefined;
  size?: "sm" | "default" | "lg" | undefined;
  /** Suppress label text on small screens (icon only). */
  iconOnly?: boolean | undefined;
}

export function ZaloShareButton({
  property,
  textOverride,
  agentName,
  agentPhone,
  className,
  variant = "secondary",
  size = "default",
  iconOnly = false,
}: ZaloShareButtonProps) {
  const user = useAuthStore((s) => s.user);
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);

    const text =
      textOverride ??
      formatPropertySummary(property, {
        agentName: agentName ?? user?.fullName,
        agentPhone,
      });

    const outcome = await shareOrCopy({
      title: property.buildingName ?? "Bất động sản",
      text,
    });

    setBusy(false);

    switch (outcome.kind) {
      case "shared":
        toast.success("Đã mở hộp thoại chia sẻ");
        return;
      case "copied":
        toast.success("Đã sao chép. Mở Zalo và dán vào hội thoại.", {
          action: {
            label: "Đang mở Zalo...",
            onClick: () => {
              if (typeof window !== "undefined") {
                window.open("https://chat.zalo.me/", "_blank", "noopener,noreferrer");
              }
            },
          },
        });
        return;
      case "cancelled":
        // User dismissed — no toast.
        return;
      case "error":
        toast.error("Không thể chia sẻ. Vui lòng thử lại.");
        return;
    }
  };

  return (
    <Button
      type="button"
      onClick={handleClick}
      disabled={busy}
      variant={variant}
      size={size}
      aria-label="Chia sẻ qua Zalo"
      className={className}
    >
      <Share2 aria-hidden="true" />
      <span className={iconOnly ? "hidden sm:inline" : ""}>Chia sẻ Zalo</span>
    </Button>
  );
}
